import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import '../styles/AdminBattleship.css';
import AdminLEDButtons from './AdminLEDButtons';

const TEAMS = ['red', 'blue', 'green', 'yellow'];
const TEAM_COLORS = {
  red: '#ff4444',
  blue: '#4444ff',
  green: '#44ff44',
  yellow: '#ffff44'
};
const SHIPS = [
  { name: 'Carrier', size: 5 },
  { name: 'Battleship', size: 4 },
  { name: 'Cruiser', size: 3 },
  { name: 'Submarine', size: 2 },
  { name: 'Destroyer', size: 1 }
];
const GRID_SIZE = 10;
const COL_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J'];

const AdminBattleship = () => {
  const [gameState, setGameState] = useState(null);
  const [connectedBoards, setConnectedBoards] = useState(0);
  const [esp32Panels, setEsp32Panels] = useState(0);
  const [selectedTeam, setSelectedTeam] = useState('red');
  const [attackingTeam, setAttackingTeam] = useState('red');
  const [targetTeam, setTargetTeam] = useState('blue');
  const [message, setMessage] = useState({ text: '', type: '' });
  const [loading, setLoading] = useState(false);
  const [log, setLog] = useState([]);

  // Ship placement state
  const [placingShip, setPlacingShip] = useState(null);
  const [horizontal, setHorizontal] = useState(true);

  // Turn order editor state
  const [editingOrder, setEditingOrder] = useState(null);

  // Ghost preview for ship placement
  const [hoveredCell, setHoveredCell] = useState(null);

  const getAdminToken = () => {
    return localStorage.getItem('adminKey') || '';
  };

  const apiCall = useCallback(async (method, endpoint, data = null, { silent = false } = {}) => {
    try {
      if (!silent) setLoading(true);
      const config = {
        headers: { 'x-admin-token': getAdminToken() }
      };

      let response;
      if (method === 'get') {
        response = await axios.get(`${API_BASE_URL}${endpoint}`, config);
      } else {
        response = await axios.post(`${API_BASE_URL}${endpoint}`, data, config);
      }

      return response.data;
    } catch (error) {
      const errorMsg = error.response?.data?.error || error.message;
      if (!silent) setMessage({ text: `Error: ${errorMsg}`, type: 'error' });
      throw error;
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  const initialLoadRef = React.useRef(true);

  const fetchGameState = useCallback(async () => {
    try {
      const data = await apiCall('get', '/api/admin/battleship/state', null, { silent: true });
      setGameState(data.game);
      setConnectedBoards(data.connectedBoards);
      if (data.game?.log) {
        setLog(data.game.log);
      }
      // Only sync attacking team on initial load — don't override admin's dropdown selection during polling
      if (data.game?.currentTurn && initialLoadRef.current) {
        setAttackingTeam(data.game.currentTurn);
        initialLoadRef.current = false;
      }

      try {
        const panelData = await apiCall('get', '/api/admin/battleship/panels', null, { silent: true });
        setEsp32Panels(panelData.count || 0);
      } catch (e) {}
    } catch (error) {}
  }, [apiCall]);

  useEffect(() => {
    fetchGameState();
    const interval = setInterval(fetchGameState, 2000);
    return () => clearInterval(interval);
  }, [fetchGameState]);

  // Auto-correct team selections when a team gets eliminated
  useEffect(() => {
    if (!gameState?.teams || gameState.phase !== 'playing') return;

    const aliveTeams = TEAMS.filter(t => !gameState.teams[t]?.isEliminated);

    // If attacking team is eliminated, switch to first alive team
    if (gameState.teams[attackingTeam]?.isEliminated) {
      const newAttacker = aliveTeams[0];
      if (newAttacker) setAttackingTeam(newAttacker);
    }

    // If target team is eliminated or same as attacker, switch to valid target
    if (gameState.teams[targetTeam]?.isEliminated || targetTeam === attackingTeam) {
      const newTarget = aliveTeams.find(t => t !== attackingTeam);
      if (newTarget) setTargetTeam(newTarget);
    }
  }, [gameState, attackingTeam, targetTeam]);

  const createGame = async () => {
    const data = await apiCall('post', '/api/admin/battleship/create');
    setGameState(data.game);
    setMessage({ text: 'Game created! Place ships for each team.', type: 'success' });
  };

  const autoPlaceAll = async () => {
    await apiCall('post', '/api/admin/battleship/auto-place-all');
    await fetchGameState();
    setMessage({ text: 'Ships placed for all teams!', type: 'success' });
  };

  const autoPlaceTeam = async (team) => {
    await apiCall('post', '/api/admin/battleship/auto-place', { team });
    await fetchGameState();
    setMessage({ text: `Ships placed for ${team}!`, type: 'success' });
  };

  const startGame = async () => {
    const data = await apiCall('post', '/api/admin/battleship/start');
    setGameState(data.game);
    setMessage({ text: 'Game started! Let the battle begin!', type: 'success' });
  };

  const endGame = async () => {
    await apiCall('post', '/api/admin/battleship/end');
    setGameState(null);
    setMessage({ text: 'Game ended.', type: 'info' });
  };

  const resyncPanels = async () => {
    try {
      const data = await apiCall('post', '/api/admin/battleship/resync-panels');
      setMessage({ text: data.message, type: 'success' });
    } catch (error) {
      // Error already shown
    }
  };

  const setTurn = async (team) => {
    try {
      await apiCall('post', '/api/admin/battleship/set-turn', { team });
      setAttackingTeam(team);
      setMessage({ text: `Turn set to ${team.toUpperCase()}`, type: 'success' });
      await fetchGameState();
    } catch (error) {
      // Error already shown
    }
  };

  const setTurnOrder = async (order) => {
    try {
      await apiCall('post', '/api/admin/battleship/set-turn-order', { order });
      setEditingOrder(null);
      setMessage({ text: `Turn order updated: ${order.join(' → ')}`, type: 'success' });
      await fetchGameState();
    } catch (error) {
      // Error already shown
    }
  };

  const placeShip = async (team, ship, x, y, isHorizontal) => {
    try {
      await apiCall('post', '/api/admin/battleship/place-ship', {
        team,
        ship,
        x,
        y,
        horizontal: isHorizontal
      });
      await fetchGameState();
      setPlacingShip(null);
      setMessage({ text: `${ship} placed for ${team}!`, type: 'success' });
    } catch (error) {
      // Error already shown
    }
  };

  const removeShip = async (team, ship) => {
    try {
      await apiCall('post', '/api/admin/battleship/remove-ship', { team, ship });
      await fetchGameState();
      setMessage({ text: `${ship} removed from ${team}`, type: 'success' });
    } catch (error) {
      // Error already shown
    }
  };

  const fireShot = async (attacker, target, x, y) => {
    try {
      const data = await apiCall('post', '/api/admin/battleship/fire', {
        attackingTeam: attacker,
        targetTeam: target,
        x,
        y
      });

      let msg = data.isHit ? `HIT at (${x},${y})!` : `Miss at (${x},${y})`;
      if (data.shipSunk) msg += ` ${data.shipSunk} SUNK!`;
      if (data.teamEliminated) msg += ` ${target} ELIMINATED!`;
      if (data.gameOver) msg += ` ${data.winner} WINS!`;

      setMessage({ text: msg, type: data.isHit ? 'success' : 'info' });

      // Auto-advance to next turn using data from server response
      if (data.nextTurn && !data.gameOver) {
        setAttackingTeam(data.nextTurn);
        // Set target to first non-attacking, non-eliminated team
        // Use eliminatedTeams from response if available, otherwise construct from known state
        const eliminatedTeams = data.teamEliminated
          ? [...(gameState?.teams ? Object.entries(gameState.teams).filter(([_, t]) => t.isEliminated).map(([name]) => name) : []), target]
          : (gameState?.teams ? Object.entries(gameState.teams).filter(([_, t]) => t.isEliminated).map(([name]) => name) : []);

        const nextTarget = TEAMS.find(t =>
          t !== data.nextTurn && !eliminatedTeams.includes(t)
        );
        if (nextTarget) setTargetTeam(nextTarget);
      }

      // Refresh state after updating local state
      await fetchGameState();
    } catch (error) {
      // Error already shown
    }
  };

  const renderGrid = (team, isTargetGrid = false) => {
    const teamData = gameState?.teams?.[team];
    if (!teamData) return null;

    // Direct mapping: admin visual grid matches physical LED board labels
    // Column A (vx=0) → gx=0 → ESP32 panel x=3 (leftmost)
    // Row 10 (vy=0) → gy=0 → ESP32 panel y=3 (topmost)
    const toGame = (vx, vy) => ({ gx: vx, gy: vy });

    const handleCellClick = (x, y) => {
      const { gx, gy } = toGame(x, y);
      if (gameState.phase === 'setup' && placingShip && !isTargetGrid) {
        placeShip(team, placingShip, gx, gy, horizontal);
      } else if (gameState.phase === 'playing' && isTargetGrid) {
        fireShot(attackingTeam, team, gx, gy);
      }
    };

    // Compute ghost ship cells for hover preview
    const getGhostCells = () => {
      if (!hoveredCell || hoveredCell.team !== team || isTargetGrid) return null;
      if (gameState.phase !== 'setup' || !placingShip || team !== selectedTeam) return null;

      const ship = SHIPS.find(s => s.name === placingShip);
      if (!ship) return null;

      const cells = [];
      let valid = true;
      for (let i = 0; i < ship.size; i++) {
        const cx = horizontal ? hoveredCell.x + i : hoveredCell.x;
        const cy = horizontal ? hoveredCell.y : hoveredCell.y + i;
        if (cx >= GRID_SIZE || cy >= GRID_SIZE) { valid = false; break; }
        const { gx, gy } = toGame(cx, cy);
        if (teamData.grid[gy][gx].type !== 'empty') valid = false;
        cells.push(`${cx},${cy}`);
      }
      return { cells: valid ? cells : cells, valid };
    };
    const ghost = getGhostCells();
    const ghostSet = ghost ? new Set(ghost.cells) : new Set();
    const ghostValid = ghost ? ghost.valid : true;

    const getCellClass = (x, y) => {
      const { gx, gy } = toGame(x, y);
      const cell = teamData.grid[gy][gx];
      const isHit = teamData.hits.some(h => h.x === gx && h.y === gy);
      const isMiss = teamData.misses.some(m => m.x === gx && m.y === gy);

      let classes = ['cell'];

      if (cell.type === 'ship' && !isTargetGrid) {
        classes.push('ship');
        if (cell.hit) classes.push('hit');
      }

      if (isHit) classes.push('hit');
      if (isMiss) classes.push('miss');

      if (ghostSet.has(`${x},${y}`)) {
        classes.push(ghostValid ? 'ghost' : 'ghost-invalid');
      }

      return classes.join(' ');
    };

    return (
      <div className={`grid-container ${teamData.isEliminated ? 'eliminated' : ''}`}>
        <div className="grid-header" style={{ backgroundColor: TEAM_COLORS[team] }}>
          {team.toUpperCase()}
          {teamData.isEliminated && ' (ELIMINATED)'}
          {gameState.winner === team && ' - WINNER!'}
        </div>
        <div className="grid">
          {/* Column headers: A-J matching physical board frame */}
          <div className="grid-row header-row">
            <div className="cell header"></div>
            {COL_LABELS.map((letter, i) => (
              <div key={i} className="cell header">{letter}</div>
            ))}
          </div>
          {/* Grid rows: 10 down to 1 matching physical board frame */}
          {[...Array(GRID_SIZE)].map((_, y) => (
            <div key={y} className="grid-row">
              <div className="cell header">{GRID_SIZE - y}</div>
              {[...Array(GRID_SIZE)].map((_, x) => (
                <div
                  key={x}
                  className={getCellClass(x, y)}
                  onClick={() => handleCellClick(x, y)}
                  onMouseEnter={() => setHoveredCell({ x, y, team })}
                  onMouseLeave={() => setHoveredCell(null)}
                  title={`${COL_LABELS[x]}${GRID_SIZE - y}`}
                />
              ))}
            </div>
          ))}
        </div>
        <div className="ships-status">
          {teamData.ships.map(ship => (
            <div
              key={ship.name}
              className={`ship-indicator ${ship.isSunk ? 'sunk' : ''}`}
            >
              {ship.name} ({ship.size}) {ship.isSunk ? '- SUNK' : `- ${ship.hits}/${ship.size}`}
              {gameState.phase === 'setup' && (
                <button
                  className="remove-ship-btn"
                  onClick={(e) => { e.stopPropagation(); removeShip(team, ship.name); }}
                  title={`Remove ${ship.name}`}
                >✕</button>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="admin-battleship">
      <h2>Portfolio Game Central Board Control</h2>

      {/* Status bar */}
      <div className="status-bar">
        <div className="status-item">
          <span className="label">Socket.IO Boards:</span>
          <span className={`value ${connectedBoards > 0 ? 'connected' : 'disconnected'}`}>
            {connectedBoards}
          </span>
        </div>
        <div className="status-item">
          <span className="label">ESP32 Panels:</span>
          <span className={`value ${esp32Panels > 0 ? 'connected' : 'disconnected'}`}>
            {esp32Panels}
          </span>
        </div>
        <div className="status-item">
          <span className="label">Game Phase:</span>
          <span className="value">{gameState?.phase || 'No Game'}</span>
        </div>
        {gameState?.currentTurn && (
          <div className="status-item">
            <span className="label">Current Turn:</span>
            <span className="value" style={{ color: TEAM_COLORS[gameState.currentTurn] }}>
              {gameState.currentTurn.toUpperCase()}
            </span>
          </div>
        )}
        {gameState?.phase === 'playing' && (
          <>
            <div className="status-item">
              <span className="label">Round:</span>
              <span className="value">{gameState.roundNumber || 1}</span>
            </div>
            <div className="status-item">
              <span className="label">Fired:</span>
              <span className="value round-shots">
                {TEAMS.filter(t => !gameState.teams[t]?.isEliminated).map(t => (
                  <span key={t} style={{ color: TEAM_COLORS[t], marginRight: 6 }}>
                    {(gameState.roundShots || []).includes(t) ? '\u2713' : '\u00B7'}{t[0].toUpperCase()}
                  </span>
                ))}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Message */}
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button onClick={() => setMessage({ text: '', type: '' })}>x</button>
        </div>
      )}

      {/* Controls */}
      <div className="controls-section">
        <h3>Game Controls</h3>
        <div className="control-buttons">
          {!gameState && (
            <button className="btn primary" onClick={createGame} disabled={loading}>
              Create New Game
            </button>
          )}

          {gameState?.phase === 'setup' && (
            <>
              <button className="btn success" onClick={autoPlaceAll} disabled={loading}>
                Auto-Place All Ships
              </button>
              <button className="btn primary" onClick={startGame} disabled={loading}>
                Start Game
              </button>
            </>
          )}

          {gameState && (
            <button className="btn danger" onClick={endGame} disabled={loading}>
              End Game
            </button>
          )}

          {/* ESP32 Panel Controls */}
          <button className="btn secondary" onClick={resyncPanels} disabled={loading}>
            Resync ESP32 Panels ({esp32Panels})
          </button>
        </div>
      </div>

      {/* Setup Phase - Ship Placement */}
      {gameState?.phase === 'setup' && (
        <div className="setup-section">
          <h3>Ship Placement</h3>
          <div className="setup-controls">
            <div className="control-group">
              <label>Team:</label>
              <select value={selectedTeam} onChange={(e) => setSelectedTeam(e.target.value)}>
                {TEAMS.map(t => (
                  <option key={t} value={t}>{t.toUpperCase()}</option>
                ))}
              </select>
              <button
                className="btn small"
                onClick={() => autoPlaceTeam(selectedTeam)}
                disabled={loading}
              >
                Auto-Place for {selectedTeam}
              </button>
            </div>

            <div className="control-group">
              <label>Ship:</label>
              <select
                value={placingShip || ''}
                onChange={(e) => setPlacingShip(e.target.value || null)}
              >
                <option value="">Select ship to place...</option>
                {SHIPS.map(s => {
                  const placed = gameState.teams[selectedTeam]?.ships.find(
                    ship => ship.name === s.name
                  );
                  return (
                    <option key={s.name} value={s.name} disabled={!!placed}>
                      {s.name} ({s.size}) {placed ? '- Placed' : ''}
                    </option>
                  );
                })}
              </select>
            </div>

            <div className="control-group">
              <label>
                <input
                  type="checkbox"
                  checked={horizontal}
                  onChange={(e) => setHorizontal(e.target.checked)}
                />
                Horizontal
              </label>
            </div>

            {placingShip && (
              <div className="placement-hint">
                Click on {selectedTeam}'s grid to place {placingShip}
              </div>
            )}
          </div>

          {/* Setup grids - show all teams */}
          <div className="grids-container setup">
            {TEAMS.map(team => (
              <div
                key={team}
                className={`team-grid ${selectedTeam === team ? 'selected' : ''}`}
                onClick={() => setSelectedTeam(team)}
              >
                {renderGrid(team, false)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Playing Phase - Fire Shots */}
      {gameState?.phase === 'playing' && (
        <div className="playing-section">
          <h3>Fire!</h3>
          <div className="fire-controls">
            <div className="control-group">
              <label>Attacking Team:</label>
              <select
                value={attackingTeam}
                onChange={(e) => setAttackingTeam(e.target.value)}
              >
                {TEAMS.filter(t => !gameState.teams[t].isEliminated).map(t => (
                  <option key={t} value={t}>{t.toUpperCase()}</option>
                ))}
              </select>
              <button
                className="btn small"
                onClick={() => setTurn(attackingTeam)}
                disabled={loading}
                title="Override server turn to match dropdown"
              >
                Set Turn
              </button>
            </div>

            <div className="control-group">
              <label>Target Team:</label>
              <select
                value={targetTeam}
                onChange={(e) => setTargetTeam(e.target.value)}
              >
                {TEAMS.filter(t => t !== attackingTeam && !gameState.teams[t].isEliminated).map(t => (
                  <option key={t} value={t}>{t.toUpperCase()}</option>
                ))}
              </select>
            </div>

            <div className="fire-hint">
              Click on {targetTeam}'s grid to fire from {attackingTeam}
            </div>
          </div>

          {/* Turn Order Editor */}
          <div className="turn-order-section">
            <div className="control-group">
              <label>Turn Order:</label>
              {!editingOrder ? (
                <>
                  <span className="turn-order-display">
                    {(gameState.turnOrder || TEAMS).map((t, i) => (
                      <span key={t}>
                        {i > 0 && ' \u2192 '}
                        <span style={{ color: TEAM_COLORS[t], fontWeight: gameState.currentTurn === t ? 'bold' : 'normal' }}>
                          {t.toUpperCase()}
                          {gameState.teams[t]?.isEliminated && ' (X)'}
                        </span>
                      </span>
                    ))}
                  </span>
                  <button
                    className="btn small"
                    onClick={() => setEditingOrder([...(gameState.turnOrder || TEAMS)])}
                  >
                    Edit
                  </button>
                </>
              ) : (
                <>
                  {editingOrder.map((t, i) => (
                    <select
                      key={i}
                      value={t}
                      onChange={(e) => {
                        const newOrder = [...editingOrder];
                        newOrder[i] = e.target.value;
                        setEditingOrder(newOrder);
                      }}
                      style={{ marginRight: 4 }}
                    >
                      {TEAMS.map(team => (
                        <option key={team} value={team}>{team.toUpperCase()}</option>
                      ))}
                    </select>
                  ))}
                  <button
                    className="btn small success"
                    onClick={() => setTurnOrder(editingOrder)}
                    disabled={loading}
                  >
                    Save
                  </button>
                  <button
                    className="btn small"
                    onClick={() => setEditingOrder(null)}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Playing grids - show all teams */}
          <div className="grids-container playing">
            {TEAMS.map(team => (
              <div
                key={team}
                className={`team-grid ${targetTeam === team ? 'target' : ''} ${attackingTeam === team ? 'attacker' : ''}`}
              >
                {renderGrid(team, team === targetTeam)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Finished Phase */}
      {gameState?.phase === 'finished' && (
        <div className="finished-section">
          <h3>Game Over!</h3>
          <div className="winner-display" style={{ color: TEAM_COLORS[gameState.winner] }}>
            {gameState.winner?.toUpperCase()} WINS!
          </div>
          <div className="grids-container finished">
            {TEAMS.map(team => (
              <div key={team} className="team-grid">
                {renderGrid(team, false)}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Game Log */}
      {log.length > 0 && (
        <div className="log-section">
          <h3>Game Log</h3>
          <div className="log-container">
            {log.slice().reverse().map((entry, i) => (
              <div key={i} className="log-entry">
                <span className="timestamp">
                  {new Date(entry.timestamp).toLocaleTimeString()}
                </span>
                <span className="message">{entry.message}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <AdminLEDButtons
        title="LED Scroll Events"
        events={[
          { label: 'Game Start', color: '#4caf50', type: 'sprite:sinking-ship', duration: 12, eventName: 'PORTFOLIO WARFARE' },
          { label: 'Turn: Red', color: '#f44336', type: 'battleship:turn', duration: 4, eventName: "RED's TURN" },
          { label: 'Turn: Blue', color: '#2196f3', type: 'battleship:turn', duration: 4, eventName: "BLUE's TURN" },
          { label: 'Turn: Green', color: '#4caf50', type: 'battleship:turn', duration: 4, eventName: "GREEN's TURN" },
          { label: 'Turn: Yellow', color: '#e6c200', type: 'battleship:turn', duration: 4, eventName: "YELLOW's TURN" },
          { label: 'HIT!', color: '#ff5722', type: 'battleship:hit', duration: 6, eventName: 'HIT!' },
          { label: 'MISS', color: '#0064c8', type: 'battleship:miss', duration: 5, eventName: 'MISS' },
          { label: 'SUNK!', color: '#d50000', type: 'battleship:sunk', duration: 7, eventName: 'SHIP SUNK!' },
          { label: 'ELIMINATED!', color: '#b71c1c', type: 'battleship:eliminated', duration: 7, eventName: 'ELIMINATED!' },
          { label: 'VICTORY!', color: '#ffd700', type: 'battleship:game-over', duration: 8, eventName: 'WINNER!' },
          { label: 'Game End', color: '#607d8b', type: 'battleship:game-end', duration: 5, eventName: 'GAME OVER' }
        ]}
      />
    </div>
  );
};

export default AdminBattleship;
